<template>
  <!-- The list beside an open session (MacMain, d40, d41): the place's
       title and "N sessions", "+", the search (it leaves the session for
       /search), then the place's rows; the open one selected. No tab strip
       and no record area. -->
  <aside class="list-column" aria-label="Sessions">
    <div class="lc-head">
      <div class="lc-titles">
        <div class="lc-title">{{ place.label }}</div>
        <div class="lc-count">{{ countText }}</div>
      </div>
      <button type="button" class="lc-plus" title="New session" aria-label="New session" @click="newSession">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
      </button>
    </div>
    <div class="lc-search">
      <button type="button" class="lc-search-btn" aria-label="Search sessions, loops, people" @click="toSearch">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="M15.5 15.5 20 20" /></svg>
        <span class="lc-search-text">Search</span>
        <span v-if="keyboardUsed" class="lc-search-key">{{ searchKey }}</span>
      </button>
    </div>
    <div ref="rows" class="lc-rows" @scroll.passive="onScroll">
      <SessionRow
        v-for="row in rows"
        :key="row.name"
        variant="list"
        :session="row"
        :me="me"
        :selected="row.name === openName"
        @open="open(row)"
        @menu="openMenu($event, row)"
      />
      <div v-if="!rows.length && !list.error && !showsEmpty" class="lc-note">Loading…</div>
      <div v-else-if="!rows.length" class="lc-note">No sessions here yet.</div>
    </div>
    <span class="lc-menu" aria-hidden="true">
      <MoreMenu ref="menu" :items="menuItems" :caption="menuCaption" :width="236" label="Session row actions" :size="1" />
    </span>
  </aside>
</template>

<script>
import SessionRow from "@/components/kit/SessionRow.vue";
import MoreMenu from "@/components/kit/MoreMenu.vue";
import sessionLists from "@/stores/sessionLists.js";
import sessionActions from "@/services/sessionActions.js";
import { placeById } from "./places.js";
import { shellState } from "./shellState.js";
import { isMac } from "./shortcuts.js";

export default {
  name: "SessionListColumn",
  components: { SessionRow, MoreMenu },
  props: {
    // mine, friends, live or liked.
    placeId: { type: String, default: "mine" },
    // The open session's name.
    openName: { type: String, default: "" },
  },
  data: () => ({ menuSession: null }),
  computed: {
    place() {
      return placeById(this.placeId) || placeById("mine");
    },
    list() {
      return sessionLists.lists[this.place.id] || sessionLists.lists.mine;
    },
    rows() {
      return this.list.rows;
    },
    // Empty only with no cursor left (M4).
    showsEmpty() {
      return sessionLists.showsEmpty(this.list);
    },
    wantsMore() {
      return sessionLists.wantsMore(this.list);
    },
    me() {
      return this.$store.state.loggedInUser?.username || null;
    },
    countText() {
      const n = this.place.id === "liked" ? sessionLists.counts.value.liked : this.list.total;
      if (!Number.isFinite(n)) return "";
      return `${n} ${n === 1 ? "session" : "sessions"}`;
    },
    keyboardUsed() {
      return shellState.keyboardUsed;
    },
    searchKey() {
      return isMac() ? "⌘K" : "Ctrl K";
    },
    menuItems() {
      return this.menuSession ? sessionActions.menuFor(this.menuSession, this.me) : [];
    },
    menuCaption() {
      return this.menuSession ? sessionActions.caption(this.menuSession) : "";
    },
  },
  watch: {
    // Too few rows to scroll, and more to come: ask (paging is scroll-only).
    wantsMore: {
      handler(more) {
        if (more) sessionLists.loadMore(this.place.id);
      },
      immediate: true,
    },
    placeId: {
      handler(id) {
        sessionLists.load(id);
        if (this.stopLive) this.stopLive();
        this.stopLive = id === "live" ? sessionLists.watchLive() : null;
      },
      immediate: true,
    },
  },
  beforeUnmount() {
    if (this.stopLive) this.stopLive();
  },
  methods: {
    open(row) {
      this.$router.replace(`/session/${encodeURIComponent(row.name)}`).catch(() => {});
    },
    openMenu(e, row) {
      this.menuSession = row;
      this.$nextTick(() => this.$refs.menu?.openAt(e.clientX, e.clientY));
    },
    newSession() {
      this.$store.dispatch("setShowDialog", true);
    },
    toSearch() {
      this.$router.push("/search").catch(() => {});
    },
    // Load more within 10 rows of the end.
    onScroll(e) {
      const el = e.target;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 60 * 10) sessionLists.loadMore(this.place.id);
    },
  },
};
</script>

<style scoped>
.list-column {
  position: sticky;
  top: 0;
  align-self: flex-start;
  height: 100dvh;
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: var(--cl-list-column);
  border-right: 1px solid var(--border-subtle);
  box-sizing: border-box;
}
.lc-head {
  height: 52px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px 0 16px;
}
.lc-titles {
  flex: 1;
  min-width: 0;
}
.lc-title {
  font: 700 15px/20px var(--cl-font);
  color: var(--cl-label);
}
.lc-count {
  min-height: 14px;
  font: 400 11px/14px var(--cl-font);
  color: var(--cl-label-3);
}
.lc-plus {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: rgba(235, 235, 245, 0.75);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.lc-plus:hover {
  background: var(--cl-hover);
}
.lc-search {
  padding: 0 12px 8px;
  flex-shrink: 0;
}
.lc-search-btn {
  width: 100%;
  height: 28px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px;
  border: none;
  border-radius: 7px;
  background: var(--cl-control-fill-pointer);
  color: var(--cl-label-50);
  cursor: text;
}
.lc-search-text {
  flex: 1;
  text-align: left;
  font: 400 13px/16px var(--cl-font);
}
.lc-search-key {
  font: 400 11px/14px var(--cl-font);
  color: var(--cl-label-4);
}
.lc-rows {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 6px 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.lc-note {
  padding: 16px 10px;
  font: 400 12px/16px var(--cl-font);
  color: var(--cl-label-3);
}
.lc-menu {
  position: fixed;
  width: 0;
  height: 0;
  overflow: hidden;
}
</style>
